import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Sparkle, TrendUp, TrendDown, GridFour, Rows, ArrowUpRight, MapPin, Buildings, GraduationCap, CurrencyInr, ArrowLeft } from "@phosphor-icons/react";
import { getStatsSummary, getNewColleges, getSeatGainers, getSeatDroppers, getCollegeHistory, ASSET_BASE } from "../lib/api";
import { Sparkline } from "../components/CollegeStatsRail";

// Deep-link support: /stats?tab=(new|increase|decrease)&year=2026
const TAB_KEYS = ["new", "increase", "decrease"];

// Match the app's palette (light theme with orange/violet accents).
const CHART_COLORS = { increase: "#6043EF", new: "#FF2A00", decrease: "#EF4444", neutral: "#71717A" };

const numberFmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));
const deltaClass = (n) => n > 0 ? "text-emerald-600" : n < 0 ? "text-rose-600" : "text-zinc-500";
const collegeTypePill = (t) => {
  const map = { govt: "bg-emerald-100 text-emerald-700", private: "bg-violet-100 text-violet-700",
                deemed: "bg-blue-100 text-blue-700", trust: "bg-amber-100 text-amber-700" };
  return map[t] || "bg-zinc-100 text-zinc-700";
};

// Row view - dense
const CollegeRow = ({ c, onSelect, selected, sr }) => (
  <button onClick={() => onSelect(c)} data-testid={`stats-row-${c.college_code}`}
    className={`grid grid-cols-12 items-center gap-3 w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-violet-50/40 transition-colors ${selected ? "bg-violet-50 border-l-4 border-l-[#6043EF]" : ""}`}>
    <div className="col-span-1 font-mono text-[11px] text-zinc-400 tabular-nums">{sr}.</div>
    <div className="col-span-4 min-w-0">
      <div className="font-semibold text-sm text-[#09090B] truncate">{c.primary_name || "—"}</div>
      <div className="text-[11px] text-zinc-500 flex items-center gap-1 truncate">
        <MapPin size={10} />{c.city || c.state || "—"} ·
        {/NMC_\d{4}_\d{4}/.test(c.college_code) ? (
          <span className="font-mono px-1 bg-[#FF2A00]/10 text-[#FF2A00] font-bold" title="Deterministic new-establishment code assigned from NMC PDF Sr.No">
            {c.college_code}
          </span>
        ) : (
          <span className="font-mono">{c.college_code}</span>
        )}
      </div>
    </div>
    <div className="col-span-2 text-xs text-zinc-600">
      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${collegeTypePill(c.college_type)}`}>
        {c.college_type || "—"}
      </span>
    </div>
    <div className="col-span-2 text-right font-mono text-sm">
      <span className="text-zinc-400">{numberFmt(c.mbbs_seats_prev)}</span>
      <span className="mx-1 text-zinc-300">→</span>
      <span className="font-bold text-[#09090B]">{numberFmt(c.mbbs_seats)}</span>
    </div>
    <div className={`col-span-2 text-right font-mono text-xs font-bold ${deltaClass(c.mbbs_seats_delta_abs)}`}>
      {c.is_new_this_year ? (
        <span className="inline-block px-2 py-0.5 bg-[#FF2A00] text-white text-[10px] uppercase tracking-wider">NEW</span>
      ) : c.mbbs_seats_delta_abs != null ? (
        <>{c.mbbs_seats_delta_abs > 0 ? "+" : ""}{c.mbbs_seats_delta_abs}
          {c.mbbs_seats_delta_pct != null && <span className="text-zinc-400 ml-1">({c.mbbs_seats_delta_pct}%)</span>}
        </>
      ) : "—"}
    </div>
    <div className="col-span-1 text-right">
      <ArrowUpRight size={14} className="text-zinc-400 inline-block" />
    </div>
  </button>
);

// Card view - jazzy
// If the college has a Google-Places / building header image we render it in the
// h-24 hero strip; otherwise we fall back to the original gradient. `img onError`
// swaps in the gradient too, so a broken URL never leaves an empty box.
const CardHero = ({ code, hasHeader, isNew, srLabel }) => {
  const [imgErr, setImgErr] = useState(false);
  const showImg = code && hasHeader && !imgErr;
  return (
    <div className="h-24 border-b-2 border-zinc-200 flex items-end p-3 relative overflow-hidden bg-gradient-to-br from-violet-100 to-orange-50">
      {showImg && (
        <img
          src={`${ASSET_BASE}/colleges/${encodeURIComponent(code)}/header`}
          alt=""
          loading="lazy"
          onError={() => setImgErr(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {/* dark scrim so the small icons stay legible on any photo */}
      {showImg && <div className="absolute inset-0 bg-black/25" />}
      <span className={`absolute top-2 left-2 font-mono text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${showImg ? "text-white bg-black/50" : "text-[#6043EF] bg-white/80"}`}>
        #{srLabel}
      </span>
      <div className="w-10 h-10 bg-[#09090B] text-white flex items-center justify-center relative z-[1]">
        <Buildings size={20} weight="fill" />
      </div>
      {isNew && (
        <span className="ml-auto self-start inline-block px-2 py-1 bg-[#FF2A00] text-white text-[10px] uppercase tracking-wider font-bold relative z-[1]">NEW</span>
      )}
    </div>
  );
};

const CollegeCard = ({ c, onSelect, selected, sr }) => (
  <button onClick={() => onSelect(c)} data-testid={`stats-card-${c.college_code}`}
    className={`text-left border-2 transition-all bg-white hover:shadow-[4px_4px_0px_0px_#6043EF] ${selected ? "border-[#6043EF] shadow-[4px_4px_0px_0px_#6043EF]" : "border-zinc-200"}`}>
    <CardHero code={c.college_code} hasHeader={c.has_header} isNew={c.is_new_this_year} srLabel={sr} />
    <div className="p-3">
      <div className="font-bold text-sm text-[#09090B] mb-0.5 line-clamp-2">{c.primary_name || "—"}</div>
      <div className="text-[11px] text-zinc-500 flex items-center gap-1 mb-2">
        <MapPin size={10} />{c.city || c.state || "—"}
      </div>
      {/NMC_\d{4}_\d{4}/.test(c.college_code) && (
        <div className="text-[10px] font-mono font-bold text-[#FF2A00] bg-[#FF2A00]/10 px-1.5 py-0.5 inline-block mb-2" title="Deterministic new-establishment code assigned from NMC PDF Sr.No">
          {c.college_code}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
        <div>
          <div className="text-zinc-400 uppercase tracking-wider">Est.</div>
          <div className="font-mono font-bold">{c.established_year || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-400 uppercase tracking-wider">Fees</div>
          <div className="font-mono font-bold flex items-center gap-0.5"><CurrencyInr size={10} />{numberFmt(c.fees_govt)}</div>
        </div>
        <div>
          <div className="text-zinc-400 uppercase tracking-wider">Seats</div>
          <div className="font-mono font-bold">{numberFmt(c.mbbs_seats)}
            {c.mbbs_seats_delta_abs > 0 && <span className="ml-1 text-emerald-600 text-[10px]">+{c.mbbs_seats_delta_abs}</span>}
          </div>
        </div>
        <div>
          <div className="text-zinc-400 uppercase tracking-wider">Type</div>
          <div className="font-mono font-bold uppercase text-[10px]">{c.college_type || "—"}</div>
        </div>
      </div>
      <div className="h-9 w-full bg-[#FF6B00] hover:bg-[#FF2A00] text-white text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 transition-colors">
        View details <ArrowUpRight size={12} weight="bold" />
      </div>
    </div>
  </button>
);

const CollegeChart = ({ college }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!college) { setHistory([]); return; }
    setLoading(true);
    getCollegeHistory(college.college_code)
      .then((r) => setHistory(r.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [college]);
  const options = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "IBM Plex Mono, monospace" },
    colors: [CHART_COLORS.increase],
    plotOptions: { bar: { borderRadius: 0, columnWidth: "50%", dataLabels: { position: "top" } } },
    dataLabels: { enabled: true, offsetY: -18, style: { fontSize: "11px", colors: ["#09090B"] } },
    xaxis: { categories: history.map((h) => `AY ${h.year}`), labels: { style: { fontSize: "11px" } } },
    yaxis: { title: { text: "MBBS seats", style: { fontSize: "11px" } } },
    grid: { borderColor: "#e4e4e7" },
    tooltip: {
      y: { formatter: (v, { dataPointIndex }) => {
        const d = history[dataPointIndex]; const delta = d?.mbbs_seats_delta_abs;
        return `${v} seats${delta ? ` (${delta > 0 ? "+" : ""}${delta} vs prev)` : ""}`;
      } },
    },
  }), [history]);
  const series = [{ name: "MBBS Seats", data: history.map((h) => h.mbbs_seats || 0) }];
  if (!college) return (
    <div className="border-2 border-zinc-200 h-72 flex items-center justify-center text-sm text-zinc-400">
      Select a college to see its multi-year seat intake
    </div>
  );
  return (
    <div className="border-2 border-zinc-200 p-4 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">SEAT INTAKE</div>
          <div className="text-sm font-bold text-[#09090B] mt-1 line-clamp-1">{college.primary_name}</div>
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">{college.college_code}</div>
      </div>
      {loading ? <div className="h-56 flex items-center justify-center text-zinc-400 text-sm">Loading…</div> :
       history.length === 0 ? <div className="h-56 flex items-center justify-center text-zinc-400 text-sm">No history</div> :
       <ReactApexChart options={options} series={series} type="bar" height={260} />}
    </div>
  );
};

// Top-of-page aggregate chart — new-vs-increase-vs-decrease bars for the last 3 years.
// Aggregates come from the summary endpoint (precomputed).
const OverviewChart = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const years = [2024, 2025, 2026];
    Promise.all(years.map((y) => getStatsSummary(y).then((r) => r.data).catch(() => null)))
      .then((res) => setRows(res.filter(Boolean)));
  }, []);
  if (rows.length === 0) return null;
  const options = {
    chart: { type: "bar", stacked: false, toolbar: { show: false }, fontFamily: "IBM Plex Mono, monospace" },
    colors: [CHART_COLORS.new, CHART_COLORS.increase, CHART_COLORS.decrease],
    plotOptions: { bar: { columnWidth: "40%", borderRadius: 0 } },
    dataLabels: { enabled: false },
    xaxis: { categories: rows.map((r) => `AY ${r.year}-${String(r.year + 1).slice(-2)}`), labels: { style: { fontSize: "11px" } } },
    grid: { borderColor: "#e4e4e7" },
    legend: { fontSize: "11px", position: "top", horizontalAlign: "left" },
  };
  const series = [
    { name: "New establishments", data: rows.map((r) => r.new_colleges || 0) },
    { name: "Intake increased",   data: rows.map((r) => r.colleges_with_increase || 0) },
    { name: "Intake decreased",   data: rows.map((r) => r.colleges_with_decrease || 0) },
  ];
  return (
    <div className="border-2 border-zinc-200 p-4 bg-white">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-2">3-YEAR OVERVIEW</div>
      <ReactApexChart options={options} series={series} type="bar" height={260} />
    </div>
  );
};

export default function StatsPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = TAB_KEYS.includes(sp.get("tab")) ? sp.get("tab") : "new";
  const initialYear = parseInt(sp.get("year") || "0", 10) || null;
  const [tab, setTab] = useState(initialTab);
  const [year, setYear] = useState(initialYear);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("row");    // "row" | "card"  (default: row)
  const [selected, setSelected] = useState(null);

  // Load summary first — resolves the latest year if not passed in URL.
  useEffect(() => {
    getStatsSummary(year || undefined).then((r) => {
      setSummary(r.data);
      if (!year) setYear(r.data.year);
    });
  }, [year]);

  // Load items whenever tab/year changes
  useEffect(() => {
    if (!year) return;
    setLoading(true);
    setSelected(null);
    const p = tab === "new" ? getNewColleges(year, true, 200)
            : tab === "increase" ? getSeatGainers(year, 200)
            : getSeatDroppers(year, 200);
    p.then((r) => setItems(r.data.colleges || [])).finally(() => setLoading(false));
  }, [tab, year]);

  // URL sync
  useEffect(() => {
    const n = new URLSearchParams();
    n.set("tab", tab);
    if (year) n.set("year", String(year));
    if (n.toString() !== sp.toString()) setSp(n, { replace: true });
  }, [tab, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { key: "new",      label: "New this year",   count: summary?.new_colleges,           icon: Sparkle, color: "text-[#FF2A00]" },
    { key: "increase", label: "Intake increased", count: summary?.colleges_with_increase, icon: TrendUp,  color: "text-[#6043EF]" },
    { key: "decrease", label: "Intake decreased", count: summary?.colleges_with_decrease, icon: TrendDown, color: "text-rose-500" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="stats-page">
      {/* Header */}
      <section className="border-b border-zinc-200 py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <button onClick={() => navigate("/")} className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-[#6043EF] mb-3 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Home
          </button>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF] mb-1">COLLEGE STATS · {year ? `AY ${year}-${String(year + 1).slice(-2)}` : ""}</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#09090B] font-heading">
            Medical colleges — year over year
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Sanctioned intake, new establishments and expansions in Indian MBBS colleges — precomputed from NMC&apos;s annual seat matrix.
          </p>
        </div>
      </section>

      {/* Charts row */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OverviewChart />
        <CollegeChart college={selected} />
      </section>

      {/* Tabs + view toggle */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between border-b-2 border-zinc-200 mb-3">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`stats-tab-${t.key}`}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.15em] border-b-2 -mb-[2px] transition-colors ${
                  tab === t.key ? "border-[#6043EF] text-[#09090B]" : "border-transparent text-zinc-500 hover:text-[#09090B]"
                }`}>
                <t.icon size={14} weight="fill" className={`inline-block mr-1 ${t.color}`} /> {t.label}
                {t.count != null && (
                  <span className="ml-2 inline-block bg-zinc-100 text-zinc-700 px-2 py-0.5 text-[10px] font-mono">{t.count}</span>
                )}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setView("card")} data-testid="view-card"
              className={`h-8 w-8 flex items-center justify-center border ${view === "card" ? "border-[#6043EF] text-[#6043EF] bg-white" : "border-zinc-300 text-zinc-500 hover:text-[#6043EF]"}`}>
              <GridFour size={14} weight="bold" />
            </button>
            <button onClick={() => setView("row")} data-testid="view-row"
              className={`h-8 w-8 flex items-center justify-center border ${view === "row" ? "border-[#6043EF] text-[#6043EF] bg-white" : "border-zinc-300 text-zinc-500 hover:text-[#6043EF]"}`}>
              <Rows size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-24 text-center text-zinc-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center text-zinc-400 text-sm">No colleges in this tab for {year}.</div>
        ) : view === "card" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-12">
            {items.map((c, i) => <CollegeCard key={c.college_code} c={c} sr={i + 1} onSelect={setSelected} selected={selected?.college_code === c.college_code} />)}
          </div>
        ) : (
          <div className="border border-zinc-200 divide-y divide-zinc-100 bg-white mb-12" data-testid="stats-row-list">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-b-2 border-zinc-200 bg-zinc-50">
              <div className="col-span-1">#</div>
              <div className="col-span-4">College</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2 text-right">Seats (prev → cur)</div>
              <div className="col-span-2 text-right">Δ</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((c, i) => <CollegeRow key={c.college_code} c={c} sr={i + 1} onSelect={setSelected} selected={selected?.college_code === c.college_code} />)}
          </div>
        )}
      </section>
    </div>
  );
}
